import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getClaim } from "../../api/getClaim";
import { getOrderLookup } from "../../api/orderLookup";
import { useTenantTheme } from "../../themes/TenantThemeProvider";
import type { Claim } from "./types";

export const useClaim = (claimNumber: string | undefined) => {
  const { setIsAppLoading } = useTenantTheme();
  const [claim, setClaim] = useState<Claim | undefined>();

  useEffect(() => {
    if (!claimNumber) {
      return undefined;
    }

    let cancelled = false;
    setIsAppLoading(true);

    const fetchClaim = async () => {
      try {
        const response = await getClaim(claimNumber);
        if (!cancelled && response.data) {
          setClaim(response.data);
        }
      } finally {
        if (!cancelled) {
          setIsAppLoading(false);
        }
      }
    };

    fetchClaim();

    return () => {
      cancelled = true;
    };
  }, [claimNumber, setIsAppLoading]);

  return { claim };
};

export const usePreFetchOrder = (onOrderLookupSuccess: (data: unknown) => void) => {
  const { setIsAppLoading } = useTenantTheme();
  const [searchParams] = useSearchParams();
  const shipInsureIdParam = searchParams.get("ship_insure_id");
  // The callback is recreated on every parent render; keep it out of the
  // effect deps so each response doesn't trigger another lookup + form reset.
  const onSuccessRef = useRef(onOrderLookupSuccess);
  onSuccessRef.current = onOrderLookupSuccess;

  useEffect(() => {
    if (!shipInsureIdParam) {
      return undefined;
    }

    let cancelled = false;
    setIsAppLoading(true);

    const prepopulateOrder = async () => {
      try {
        const response = await getOrderLookup({ shipInsureId: shipInsureIdParam });
        if (!cancelled) {
          onSuccessRef.current(response.data);
        }
      } catch {
        // silently fail
      } finally {
        if (!cancelled) {
          setIsAppLoading(false);
        }
      }
    };

    prepopulateOrder();

    return () => {
      cancelled = true;
      setIsAppLoading(false);
    };
  }, [setIsAppLoading, shipInsureIdParam]);
};

export const useScreenAnalytics = (activeStep: number) => {
  useEffect(() => {
    const stepMap = {
      0: "Order Lookup",
      1: "Claim Details",
      2: "Claim Review",
      3: "Submitted",
    };
    void stepMap[activeStep as keyof typeof stepMap];
  }, [activeStep]);
};
